/**
 * ModernDashboardOverview - Enhanced dashboard with modern statistics cards and next event banner
 */
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Calendar, Users, Euro, Utensils, Leaf, Mail, Clock, Ticket, Image as ImageIcon } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { useAdmin } from './AdminHooks';
import { cn } from '../../lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { adminAPI } from './AdminUtils';
import NotificationsCenter from './NotificationsCenter';

const StatCard = ({ title, value, icon: Icon, description, className }) => (
  <Card className={cn("bg-slate-900 border-slate-700", className)}>
    <CardContent className="p-4">
      <div className="flex flex-col gap-2 items-center text-center">
        <div className="flex justify-center items-center w-10 h-10 rounded-full bg-slate-800">
          <Icon className="w-4 h-4 text-blue-400" />
        </div>
        <div className="text-xs font-medium text-slate-300">{title}</div>
        <div className="text-2xl font-bold text-white">{value}</div>
        {description && (
          <div className="text-xs text-slate-400">{description}</div>
        )}
      </div>
    </CardContent>
  </Card>
);

const NextEventBanner = ({ nextEvent, toggleReservations, updatingEvent }) => {
  if (!nextEvent) return null;

  return (
    <Card className={cn(
      "relative inline-block overflow-hidden border-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 shadow-2xl",
      nextEvent.reservationsOpen ? "from-green-600 via-emerald-600 to-teal-600" : "from-slate-700 via-slate-800 to-slate-900"
    )}>
      <div className="absolute inset-0 bg-gradient-to-r to-transparent from-black/20" />
      <CardContent className="relative p-4">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3 items-center">
            <div className={cn(
              "p-2.5 rounded-xl bg-white/10 backdrop-blur-sm",
              nextEvent.reservationsOpen ? "bg-green-500/20" : "bg-slate-700/50"
            )}>
              <Calendar className={cn(
                "h-6 w-6",
                nextEvent.reservationsOpen ? "text-green-300" : "text-slate-300"
              )} />
            </div>
            <div>
              <div className="flex gap-2 items-center mb-1">
                <h3 className="text-lg font-bold text-white">
                  Vol.{nextEvent.volume} — {nextEvent.theme}
                </h3>
              </div>
              <div className="flex gap-1.5 items-center">
                <Badge className="inline-flex gap-1 items-center text-white bg-white/10 border-white/20">
                  <Calendar className="w-3 h-3" />
                  {nextEvent.date}
                </Badge>
                <Badge className="inline-flex gap-1 items-center text-white bg-white/10 border-white/20">
                  <Clock className="w-3 h-3" />
                  {nextEvent.time}
                </Badge>
                {(nextEvent.price !== undefined && nextEvent.price !== null) && (
                  <Badge className="inline-flex gap-1 items-center text-white bg-white/10 border-white/20">
                    <Ticket className="w-3 h-3" />
                    {nextEvent.price}€
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end items-center pt-2 w-full border-t border-white/20">
            <Badge className={cn(
              "text-xs",
              nextEvent.reservationsOpen 
                ? "bg-green-500/20 text-green-300 border-green-500/30" 
                : "bg-slate-600/20 text-slate-300 border-slate-600/30"
            )}>
              {nextEvent.reservationsOpen ? 'Réservations ouvertes' : 'Réservations fermées'}
            </Badge>
            <Switch
              checked={nextEvent.reservationsOpen}
              onCheckedChange={() => toggleReservations(nextEvent.id, nextEvent.reservationsOpen)}
              disabled={updatingEvent}
              className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SmallCard = ({ title, value }) => (
  <Card className="bg-slate-900 border-slate-700">
    <CardHeader className="pb-1">
      <CardTitle className="text-xs text-slate-300">{title}</CardTitle>
    </CardHeader>
    <CardContent className="pt-1">
      <div className="text-xl font-bold text-white">{value}</div>
    </CardContent>
  </Card>
);

const ModernDashboardOverview = ({ className }) => {
  const { nextEvent, stats, clients, events, reservations, toggleReservations, updatingEvent } = useAdmin();

  const menuTotals = useMemo(() => {
    const counts = {};
    (reservations || []).forEach(r => {
      if (Array.isArray(r.menuSelections) && r.menuSelections.length) {
        r.menuSelections.forEach(s => {
          const name = s.name;
          const qty = s.quantity || 0;
          counts[name] = (counts[name] || 0) + qty;
        });
      } else {
        // Old menuChoice values ('meat'/'vegetarian') are treated as Standard/no-item and not counted
      }
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [reservations]);
  const maxMenuTotal = useMemo(() => (menuTotals.length ? menuTotals[0][1] : 0), [menuTotals]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuMap, setMenuMap] = useState({});
  useEffect(() => {
    const loadMenu = async () => {
      try {
        if (!nextEvent?.id) { setMenuMap({}); return; }
        const res = await adminAPI.get(`/menus/${nextEvent.id}`);
        const dishes = res.data?.menu?.dishes || [];
        const map = {};
        dishes.forEach(d => { if (d?.name) map[d.name] = { type: d.type || 'other', vegetarian: !!d.vegetarian }; });
        setMenuMap(map);
      } catch {
        setMenuMap({});
      }
    };
    loadMenu();
  }, [nextEvent?.id]);
  const groupedTotals = useMemo(() => {
    const groups = { starter: [], main: [], dessert: [], other: [] };
    menuTotals.forEach(([name, total]) => {
      const info = menuMap[name];
      const t = info?.type || 'other';
      (groups[t] || groups.other).push({ name, total });
    });
    const label = (t) => t === 'starter' ? 'Entrées' : t === 'main' ? 'Plats' : t === 'dessert' ? 'Desserts' : 'Divers';
    return Object.entries(groups)
      .filter(([, items]) => items.length > 0)
      .map(([t, items]) => ({ type: t, label: label(t), items }));
  }, [menuTotals, menuMap]);

  

  return (
    <div className={cn("mb-6 space-y-6", className)}>
      <div className="grid gap-4 md:grid-cols-2">
        {/* <NotificationsCenter /> */}
        {/*<NextEventBanner 
          nextEvent={nextEvent} 
          toggleReservations={toggleReservations} 
          updatingEvent={updatingEvent} 
        />*/}
      </div>
    </div>
  );
};

ModernDashboardOverview.propTypes = {
  className: PropTypes.string,
};

export default ModernDashboardOverview;
